import { useEffect, useState } from 'react';
import { useTable, usePagination, useFlexLayout } from 'react-table'
import { Button } from 'react-bootstrap';
import './PagerTable.css'
import FilterBar, { Filter } from '../widgets/FilterBar';

interface PagerTableProps {
  getData: (pageSize: number, pageIndex: number, sort?: SortField[], filter?: Filter[]) => void,
  columns: any,
  tableData: any
  refresh: number
  showPager?: boolean
  showFilter?: boolean 
}


export interface SortField {
  sortBy: string,
  sortDir: "ASC"|"DES"|"NONE"
}

const PagerTable = (props: PagerTableProps) => {

  const [pageCount, setPageCount] = useState<number>(0);
  const [sortFields, setSortFields] = useState <SortField[]>([])
  const [goto, setGoto] = useState("1")
  const [filters, setFilters] = useState<Filter[]>([])

  const {
    getData,
    columns,
    tableData,
    refresh,
    showPager = props.showPager === undefined ? true : props.showPager,
    showFilter
  } = props;


  // when data changes... adjust page count according to data. data contains full response 
  // object so it includes the total records, plus the data being displayed right now. 
  useEffect(() => {
    if (tableData) {
      setPageCount(Math.ceil(tableData.count / pageSize));

      // detect if current page exceeds # of records in table... may have to set back to page 1
      if (tableData.count < (pageSize * pageIndex) && pageIndex > 0) doGotoPage(0)
    }
    else {
      setPageCount(0);
    }
  }, [tableData])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable({
    columns,
    data: tableData.data,
    initialState: { pageIndex: 0, pageSize: 10 },
    manualPagination: true,
    pageCount: pageCount
  }, usePagination, useFlexLayout);


  // Wrapper for gotoPage... because when we programatically goto page we also need to update the 
  // Goto input. 
  const doGotoPage = (pageNum: number) => {
    setGoto(`${pageNum + 1}`)
    gotoPage(pageNum);
  }

  // when size/page changes occur... initiate refetch of data. 
  // also do on initial time
  useEffect(() => {
    console.log(`PagerTable effect:pageIndex:${pageIndex} pageSize:${pageSize} refesh:${tableData.refresh} `)
    // skip sortfields that have neutral sort direction
    var sorts = sortFields.filter(s => s.sortDir !== "NONE")
    getData(pageSize, pageIndex, sorts, filters);
  }, [pageIndex, pageSize, refresh, sortFields, filters]);


  let isEven = false;
  const getRowColor = () => {
    isEven = !isEven;
    if (isEven) return "evenrow"
    else return "oddrow";
  }


  const onSortChange = (e: any) => {
    if (Object.keys(e).includes("sortBy")) {
      // If sortBy was provided... 
      console.log("SORT BY THIS:", e.sortBy)
      const sorts = [...sortFields] // copy
      const idx = sorts.findIndex(s => s.sortBy === e.sortBy)       
      if (idx > -1) {
        // already exists
        var sf = { ...sorts[idx] }
        if (sf.sortDir === "ASC") sf.sortDir = "DES"
        else if (sf.sortDir === "DES") sf.sortDir = "NONE"
        else sf.sortDir = "ASC"        
        sorts[idx] = sf        
        setSortFields(sorts)
      }
      else {
        const sorts = [...sortFields] // copy
        var sf = {
          sortBy: e.sortBy,
          sortDir: "ASC"
        } as SortField
        sorts.push(sf)
        setSortFields(sorts)
      }      
    }
  }

  const renderSortIcon = (e: any) => {
    if (Object.keys(e).includes("sortBy")) {

      const srt = sortFields.find(s => s.sortBy === e.sortBy) 
      if (srt) {
        if (srt.sortDir === "ASC") return <i className="ms-2 far fa-caret-square-up" />
        else if (srt.sortDir === "DES") return <i className="ms-2 far fa-caret-square-down" />
        else return <></>
      }
      return <></>
    }
    else <>^</> // no sort enabled     
  }


  const onFilterChange = (filt: Filter[]) => {
    setFilters(filt)
    console.log("On filter change!")
  }

  const renderPageNums = () => {    
    
    let startRange = (pageIndex + 1) - 7
    if (startRange < 1) startRange = 1; 
    let max = startRange + 10 

    let resp:JSX.Element[] = [];
    for (let i = startRange; i <= max && i <= pageCount; i++) {
      var cn = "pageNumber" + (i === pageIndex + 1 ? " pageNumberSel" : "")
      resp.push(<div key={i} className={cn} onClick={() => doGotoPage(i-1) } >{i}</div>)
    }
 
    return resp
  }


  return (
    <div>
      {showFilter  && <FilterBar onFilterChange={onFilterChange} columns={columns} />}

      <div {...getTableProps()} className='table table-striped table-bordered border-primary mytable' style={{ width: "100%" }} >
        <div>
          {// Loop over the header rows
            headerGroups.map((headerGroup: any) => {
              // Apply the header row props
              return <div className="header"  {...headerGroup.getHeaderGroupProps()}>
                {// Loop over the headers in each row
                  headerGroup.headers.map((column: any) => {
                    // Apply the header cell props
                    return <div className="sortCol" onClick={() => onSortChange(column)} {...column.getHeaderProps()}>
                      {// Render the header
                        column.render('Header')
                      }
                      {renderSortIcon(column)}
                    </div>
                  }
                  )}
              </div>
            }

            )}
        </div>
        <div  {...getTableBodyProps()} >
          {// Loop over the table rows
            rows.map((row: any) => {
              // Prepare the row for display
              prepareRow(row)
              return (
                // Apply the row props
                <div {...row.getRowProps()} className={getRowColor()}    >
                  {// Loop over the rows cells
                    row.cells.map((cell: any) => {
                      // Apply the cell props
                      return (
                        <div {...cell.getCellProps()}   >
                          {// Render the cell contents
                            cell.render('Cell')}
                        </div>
                      )
                    })}
                </div>
              )
            })}
        </div>
      </div>

      {/* Pagination interface */}
      {!showPager ? null :

        <div className="pagination">
          <Button onClick={() => doGotoPage(0)} disabled={!canPreviousPage} className="rbPrimaryIcon-custom pagerButton" >
            <i className="fas fa-step-backward" /> 
          </Button>{' '}&nbsp;
          <Button onClick={() => previousPage()} disabled={!canPreviousPage} className="rbPrimaryIcon-custom pagerButton">
            <i className="fas fa-caret-left"></i>
          </Button>{' '}&nbsp;
          {renderPageNums() }
          <Button onClick={() => nextPage()} disabled={!canNextPage} className="rbPrimaryIcon-custom pagerButton" >
            <i className="fas fa-caret-right"></i>
          </Button>{' '}&nbsp;
          <Button onClick={() => doGotoPage(pageCount - 1)} disabled={!canNextPage} className= "rbPrimaryIcon-custom pagerButton">
            <i className="fas fa-step-forward" /> 
          </Button>{' '}&nbsp;
          <div style={{ "paddingTop": "7px" }}>
            <label className="form-label">Page</label>{' '}
            <strong>
              {pageIndex + 1} / {pageCount}
            </strong>{' '}
          </div>
          <div className='leadspc'>
            <label className="form-label">Go to:</label>{' '}
            <input
              style={{ "width": "70px", "marginRight": "5px" }}
              className='form-control-sm'
              type="number"
              title="Go to"
              value={goto}
              onChange={(e) => setGoto(e.target.value)}
              onBlur={e => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0                
                doGotoPage(page)
              }} 
            />
          </div>
          <select
            className="form-select-sm"
            value={pageSize}
            title="Page size"
            onChange={e => {
              setPageSize(Number(e.target.value))
            }}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>

          <div className="ms-3" >Total Records {tableData?.count}</div>

        </div>
        // end pagination  
      }

    </div>

  )


}

export default PagerTable